import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tag, Card, Typography, Popconfirm, Spin, Empty } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

import { useAntdTheme, useNotification } from "../../shared/hooks";
import { listUsers, removeUser, sendUserInvite, RestaurantUser } from "./api";
import { CreateUserModal } from "./CreateUserModal";
import { EditUserRolesModal } from "./EditUserRolesModal";

const { Title, Text } = Typography;

interface UserManagementProps {
  restaurantId: string;
}

export const UserManagement: React.FC<UserManagementProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { showSuccess, notifyError } = useNotification();

  const [users, setUsers] = useState<RestaurantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editRolesModalOpen, setEditRolesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RestaurantUser | null>(null);

  const currentUserEmail = sessionStorage.getItem("username");

  const fetchUsers = () => {
    setLoading(true);
    listUsers(
      restaurantId,
      (data) => {
        setUsers(data.users || []);
        setLoading(false);
      },
      (err) => {
        notifyError(t("failed_to_fetch_users"), err);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [restaurantId]);

  const handleRemoveUser = (user: RestaurantUser) => {
    removeUser(
      restaurantId,
      user.user_id,
      () => {
        showSuccess(t("user_removed_successfully"));
        fetchUsers();
      },
      (err) => {
        notifyError(t("failed_to_remove_user"), err);
      }
    );
  };

  const handleSendInvite = (user: RestaurantUser) => {
    sendUserInvite(
      restaurantId,
      user.user_id,
      () => {
        showSuccess(t("invite_sent_successfully"));
      },
      (err) => {
        notifyError(t("failed_to_send_invite"), err);
      }
    );
  };

  const handleEditRoles = (user: RestaurantUser) => {
    setSelectedUser(user);
    setEditRolesModalOpen(true);
  };

  const getRoleColor = (role: string): string => {
    switch (role) {
      case "Admin":
        return "red";
      case "Manager":
        return "blue";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<RestaurantUser> = [
    {
      title: t("email"),
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <Space>
          <UserOutlined />
          <Text>{email}</Text>
          {email === currentUserEmail && <Tag color="green">{t("you")}</Tag>}
        </Space>
      ),
    },
    {
      title: t("roles"),
      dataIndex: "roles",
      key: "roles",
      render: (roles: string[] | null) => (
        <Space>
          {(roles || []).map((role) => (
            <Tag key={role} color={getRoleColor(role)}>
              {t(`role_${role.toLowerCase()}`)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      width: 280,
      render: (_: unknown, record: RestaurantUser) => {
        const isCurrentUser = record.email === currentUserEmail;
        return (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditRoles(record)}
              title={t("edit_roles")}
            />
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleSendInvite(record)}
              title={t("send_invite")}
            />
            <Popconfirm
              title={t("remove_user_confirm")}
              description={t("remove_user_description")}
              onConfirm={() => handleRemoveUser(record)}
              okText={t("yes")}
              cancelText={t("no")}
              disabled={isCurrentUser}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isCurrentUser}
                title={isCurrentUser ? t("cannot_remove_yourself") : t("remove_user")}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          marginBottom: token.marginLG,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginBottom: token.marginMD,
          }}
        >
          <Space>
            <UserOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Title level={4} style={{ margin: 0 }}>
              {t("user_management")}
            </Title>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            {t("add_user")}
          </Button>
        </Space>

        {loading ? (
          <div style={{ textAlign: "center", padding: token.paddingXL }}>
            <Spin size="large" />
          </div>
        ) : users.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_users_available")} />
        ) : (
          <Table columns={columns} dataSource={users} rowKey="user_id" pagination={false} />
        )}
      </Card>

      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        restaurantId={restaurantId}
        onSuccess={() => {
          setCreateModalOpen(false);
          fetchUsers();
        }}
      />

      <EditUserRolesModal
        open={editRolesModalOpen}
        onClose={() => {
          setEditRolesModalOpen(false);
          setSelectedUser(null);
        }}
        restaurantId={restaurantId}
        user={selectedUser}
        onSuccess={() => {
          setEditRolesModalOpen(false);
          setSelectedUser(null);
          fetchUsers();
        }}
      />
    </div>
  );
};
